/**
 * Panel nástrojů Codable tahu — kam co patří.
 *
 * Původní UI kreslilo-tool aktivitu na dvou místech: nativní tool cally (SSE
 * `tool_call`/`tool_result`) jako chips uvnitř assistant bubliny a výsledky
 * fence nástrojů (```tool_read```, ```tool_list```, …) jako **falešné user
 * zprávy** s prefixem „Výsledky nástrojů:" plovoucí mid-stream. Uživatel
 * neviděl jednu sekci „co agent udělal", ale texty rozmstěné v proudu.
 *
 * Oprava: fence výsledky mají svoje místo v panelu nástrojů agentova tahu,
 * vedle nativních chips. Protokol (text s prefixem + instrukce pro model)
 * zůstává v LLM payloadu — mění se jen to, KTERÝ text se ukazuje člověku.
 */

/** Průvodce auto-followupu s výsledky fence nástrojů. */
export type ToolOutputMeta = {
  tools: string[];
  /** Krátký text pro LLM místo plného výstupu (šetří kontext). */
  summary: string;
  /** Uživatelsky čitelná část — renderuje se v panelu nástrojů, ne jako user
   * bublina. Řádek bez ní je stará uložená historie → samostatný render. */
  body?: string;
};

/** Výsledky fence nástrojů vtělené do panelu nástrojů jednoho agentova tahu. */
export type HostedToolOutput = { bodies: string[]; tools: string[] };

/** Výsledky mapování řádků nástrojů na jejich hostitele (assistant zprávu). */
export interface ToolOutputHosting {
  /** id assistant zprávy → její vtělené výstupy */
  byHost: Map<string, HostedToolOutput>;
  /** id tool-output řádků, na které už ukazuje hostitel (vyřadit z proudu) */
  hostedIds: Set<string>;
}

/** Minimum z Message, které hostování potřebuje. */
type HostableRow = {
  id: string;
  role: "user" | "assistant";
  toolOutput?: ToolOutputMeta;
};

/**
 * Výsledky fence nástrojů (řádky s `toolOutput.body`) patří do panelu nástrojů
 * předchozího assistantova tahu — tam, kde jsou i nativní chips. Řádek bez
 * předchůdce se hostit nedá (nebylo by kam), ten se renderuje samostatně.
 */
export function hostToolOutputs(rows: HostableRow[]): ToolOutputHosting {
  const byHost = new Map<string, HostedToolOutput>();
  const hostedIds = new Set<string>();
  let host: string | null = null;
  for (const m of rows) {
    if (m.role === "assistant") {
      host = m.id;
      continue;
    }
    const body = m.toolOutput?.body?.trim();
    if (!body || !host) continue;
    const prev = byHost.get(host);
    const tools = m.toolOutput?.tools ?? [];
    if (prev) {
      prev.bodies.push(body);
      prev.tools.push(...tools);
    } else {
      byHost.set(host, { bodies: [body], tools: [...tools] });
    }
    hostedIds.add(m.id);
  }
  return { byHost, hostedIds };
}

/**
 * Ořízne protokolový prefix z textu, který se renderuje člověku. Prefix je
 * součást domluvy s modelem (a je v uložené historii), ne sdělení pro uživatele.
 */
export function stripToolFollowupPrefix(
  content: string,
  prefixes: string[],
): string {
  const c = content.replace(/^\s+/, "");
  for (const p of prefixes) {
    if (p && c.startsWith(p)) return c.slice(p.length).replace(/^[\s:]+/, "");
  }
  return c;
}
